import React, { useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../../firebase-config";
import { TableVirtuoso } from "react-virtuoso";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function Brokers({ year }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDocVisible, setLastDocVisible] = useState();

  const fetchMoreData = useCallback(() => {
    async function getNextDocuments() {
      const q = query(
        collection(db, "insurance-brokers"),
        where("year", "==", year),
        orderBy("name"),
        limit(10),
        startAfter(lastDocVisible)
      );

      const allDocs = [];
      const querySnapshot = await getDocs(q);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      querySnapshot.forEach((doc) => {
        allDocs.push(doc.data());
      });
      setData((d) => [...d, ...allDocs]);
      setLastDocVisible(lastVisible);
    }
    getNextDocuments();
  }, [year, setData, lastDocVisible]);

  useEffect(() => {
    async function getFirstDocuments() {
      setLoading(true);
      const q = query(
        collection(db, "insurance-brokers"),
        where("year", "==", year),
        orderBy("name"),
        limit(10)
      );
      const allDocs = [];
      const querySnapshot = await getDocs(q);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      querySnapshot.forEach((doc) => {
        allDocs.push(doc.data());
      });

      setData(allDocs);
      setLastDocVisible(lastVisible);
      setLoading(false);
    }
    getFirstDocuments();
  }, [year]);

  return loading ? (
    <h1>Loading</h1>
  ) : (
    <TableVirtuoso
      style={{ height: 600 }}
      data={data}
      endReached={fetchMoreData}
      components={{
        Table: (props) => (
          <Table
            {...props}
            stickyHeader
            aria-label="customized table"
          />
        ),
        TableHead: TableHead,
        TableRow: StyledTableRow,
        TableBody: React.forwardRef((props, ref) => (
          <TableBody {...props} ref={ref} />
        )),
        EmptyPlaceholder: <h6>New Items Loading .....</h6>,
      }}
      fixedHeaderContent={() => (
        <TableRow>
          <StyledTableCell>Name of Broker</StyledTableCell>
          <StyledTableCell align="right">Registration No.</StyledTableCell>
          <StyledTableCell align="right">Licence Date</StyledTableCell>
        </TableRow>
      )}
      itemContent={(index, broker) => (
        <>
          <StyledTableCell component="th" scope="broker" key={index}>
            {broker.name}
          </StyledTableCell>
          <StyledTableCell align="right">
            {broker["registration-number"]}
          </StyledTableCell>
          <StyledTableCell align="right">
            {broker["date-licenced"].toDate().toDateString()}
          </StyledTableCell>
        </>
      )}
    />
  );
}
